/**
 * Static data + value contract for the Create Promo Code modal. Lives next
 * to the component so the form, its tests, and any future Storybook stories
 * pull from a single source of truth (same convention as
 * `contactInformationData.ts`).
 */

import { AppStrings } from "../../../../../utils/constants/AppStrings";

export type IosLinkBehavior = "browser" | "app";
export type IosIfNotInstalled = "app_store" | "custom_url";
export type AndroidLinkBehavior = "browser" | "app";
export type AndroidIfNotInstalled = "google_play" | "custom_url";

export type CreatePromoCodeFormValues = {
  name: string;
  bonusSelection: string[];
  createAppsOnAirLink?: boolean;
  appLinkName: string;
  deepLinkUrl: string;
  iosLinkBehavior: IosLinkBehavior;
  iosIfNotInstalled: IosIfNotInstalled;
  iosCustomUrl?: string;
  androidLinkBehavior: AndroidLinkBehavior;
  androidIfNotInstalled: AndroidIfNotInstalled;
  androidCustomUrl?: string;
  previewTitle?: string;
  previewImageUrl?: string;
  previewDescription?: string;
};

export const CREATE_PROMO_CODE_INITIAL_VALUES: CreatePromoCodeFormValues = {
  name: "",
  bonusSelection: [],
  createAppsOnAirLink: true,
  appLinkName: "",
  deepLinkUrl: "",
  iosLinkBehavior: "browser",
  iosIfNotInstalled: "app_store",
  iosCustomUrl: "",
  androidLinkBehavior: "browser",
  androidIfNotInstalled: "google_play",
  androidCustomUrl: "",
  previewTitle: "",
  previewImageUrl: "",
  previewDescription: "",
};

/**
 * Fields that gate the `Save Code` CTA. Mirrors the `required: true` rules
 * on the form — keep the two in sync. Radio groups always carry a default
 * value so they're not part of the gate.
 */
export const CREATE_PROMO_CODE_REQUIRED_FIELDS: ReadonlyArray<
  keyof CreatePromoCodeFormValues
> = ["name", "bonusSelection", "appLinkName", "deepLinkUrl"];

/**
 * "Filled" predicate used by the Save Code gate. Covers the two value
 * shapes the required fields can take:
 *   - Strings (`name`, `appLinkName`, `deepLinkUrl`) — non-empty after
 *     trimming, so all-whitespace input doesn't unlock the CTA.
 *   - String arrays (`bonusSelection`) — at least one selection.
 * Anything else (undefined, numbers, objects) counts as not filled,
 * matching the "must be present" semantics of `{ required: true }`.
 */
export const isPromoCodeFieldFilled = (value: unknown): boolean => {
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return false;
};

export type RadioOption<TValue extends string> = {
  value: TValue;
  label: string;
};

/**
 * Bonus types selectable from the Reward section. The set below is a
 * placeholder catalogue — the real list lives on the BE and ships in a
 * follow-up slice. Replace this array with `[{ value, label }]` entries
 * from the lookup endpoint once it lands; nothing else in the modal
 * needs to change because the form already roundtrips arbitrary string
 * values via `bonusSelection: string[]`.
 */
export const getBonusSelectionOptions = (): ReadonlyArray<{
  value: string;
  label: string;
}> => [
  {
    value: "sportsbook_free_cash",
    label: AppStrings.PAM_AFFILIATE_PROMO_REWARD_SPORTSBOOK_FREE_CASH,
  },
  {
    value: "casino_deposit_match",
    label: AppStrings.PAM_AFFILIATE_PROMO_REWARD_CASINO_DEPOSIT_MATCH,
  },
];

export const getIosLinkBehaviorOptions = (): ReadonlyArray<
  RadioOption<IosLinkBehavior>
> => [
  { value: "browser", label: AppStrings.PROMO_CODE_LINK_BEHAVIOR_BROWSER },
  { value: "app", label: AppStrings.PROMO_CODE_LINK_BEHAVIOR_IOS_APP },
];

export const getIosIfNotInstalledOptions = (): ReadonlyArray<
  RadioOption<IosIfNotInstalled>
> => [
  {
    value: "app_store",
    label: AppStrings.PROMO_CODE_IF_NOT_INSTALLED_APP_STORE,
  },
  {
    value: "custom_url",
    label: AppStrings.PROMO_CODE_IF_NOT_INSTALLED_CUSTOM_URL,
  },
];

export const getAndroidLinkBehaviorOptions = (): ReadonlyArray<
  RadioOption<AndroidLinkBehavior>
> => [
  { value: "browser", label: AppStrings.PROMO_CODE_LINK_BEHAVIOR_BROWSER },
  {
    value: "app",
    label: AppStrings.PROMO_CODE_LINK_BEHAVIOR_ANDROID_APP,
  },
];

export const getAndroidIfNotInstalledOptions = (): ReadonlyArray<
  RadioOption<AndroidIfNotInstalled>
> => [
  {
    value: "google_play",
    label: AppStrings.PROMO_CODE_IF_NOT_INSTALLED_GOOGLE_PLAY,
  },
  {
    value: "custom_url",
    label: AppStrings.PROMO_CODE_IF_NOT_INSTALLED_CUSTOM_URL,
  },
];
